<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from '@martyrs/src/design-system/components/Icon/Icon.vue';

const { t, locale } = useI18n();

// Props
const props = defineProps({
  notification: {
    type: Object,
    required: true
  }
});

// Compute if notification is unread
const isUnread = computed(() => props.notification.status !== 'read');

// Format date
const formattedDate = computed(() => {
  const date = new Date(props.notification.createdAt);
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSec < 60) {
    return t('notifications.item.justNow');
  } else if (diffMin < 60) {
    return t('notifications.item.minutesAgo', { count: diffMin });
  } else if (diffHours < 24) {
    return t('notifications.item.hoursAgo', { count: diffHours });
  } else if (diffDays < 7) {
    return t('notifications.item.daysAgo', { count: diffDays });
  } else {
    return date.toLocaleDateString(locale.value);
  }
});

// Emit click event
const emit = defineEmits(['click']);
const handleClick = () => {
  emit('click', props.notification);
};
</script>

<template>
  <div
    class="notification-item gap-thin flex pd-small radius-small bg-card cursor-pointer"
    :class="{ 'bg-accent-secondary-nano': isUnread }"
    @click="handleClick"
  >
    <div class="notification-content">
      <p class="text fw-medium mn-b-small">{{ notification.title }}</p>
      <p class="footnote mn-b-small">{{ notification.body }}</p>
      <p class="footnote flex-center flex-nowrap flex gap-micro pd-thin w-max radius-thin bg-base">
        <Icon name="calendar" class="i-small" />
        {{ formattedDate }}
      </p>
    </div>
    <div v-if="isUnread" class="flex-child-default unread-indicator"></div>
  </div>
</template>

<style scoped>
.unread-indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: var(--primary-color, #0066cc);
  align-self: center;
  margin-left: 0.5rem;
}
</style>